import React, { useState } from 'react';
import StylingMockLayout from '../shared/StylingMockLayout';
import HeroSection from 'components/container/HeroSection';
import CTASection from 'components/container/CTASection/CTASection';
import FeatureGrid from 'components/container/FeatureGrid';
import PrimaryButton from 'components/button/PrimaryButton';
import SecondaryButton from 'components/button/SecondaryButton';
import TabNav from 'components/ui/TabNav/TabNav';
import SidebarNav from 'components/ui/SidebarNav/SidebarNav';
import './StylingMockLayouts.css';

/**
 * Dashboard Layout Example Component
 * Shows complete dashboard with tabs and sidebar
 */
const DashboardLayoutExample = () => {
  const [activeTab, setActiveTab] = useState('resources');
  const [activePath, setActivePath] = useState('/overview');

  const sidebarItems = [
    { id: 'overview', label: 'Overview', path: '/overview' },
    { id: 'analytics', label: 'Analytics', path: '/analytics' },
    { id: 'settings', label: 'Settings', path: '/settings' },
    { id: 'reports', label: 'Reports', path: '/reports' },
    { id: 'help', label: 'Help', path: '/help' }
  ];

  const tabs = [
    { id: 'resources', label: 'Resources', count: 12 },
    { id: 'costs', label: 'Cost Pools', count: 5 },
    { id: 'usage', label: 'Usage', count: 8 }
  ];

  return (
    <div className="page-layout-sample dashboard-layout">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <div className="dashboard-actions">
          <PrimaryButton onClick={() => {}}>New Item</PrimaryButton>
        </div>
      </div>
      <div className="dashboard-content">
        <aside className="dashboard-sidebar">
          <SidebarNav
            items={sidebarItems}
            activePath={activePath}
            onNavigate={(path) => {
              setActivePath(path);
            }}
            grouped={false}
          />
        </aside>
        <main className="dashboard-main">
          <TabNav
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={(tabId) => {
              setActiveTab(tabId);
            }}
          />
          <div className="dashboard-stats">
            <div className="stat-card">
              <div className="stat-value">1,234</div>
              <div className="stat-label">Total Users</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">$45,678</div>
              <div className="stat-label">Revenue</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">89%</div>
              <div className="stat-label">Uptime</div>
            </div>
          </div>
          <div className="dashboard-section">
            <h2>Recent Activity</h2>
            <div className="activity-list">
              <div className="activity-item">
                <div className="activity-icon">📊</div>
                <div className="activity-content">
                  <div className="activity-title">New report generated</div>
                  <div className="activity-time">2 hours ago</div>
                </div>
              </div>
              <div className="activity-item">
                <div className="activity-icon">👤</div>
                <div className="activity-content">
                  <div className="activity-title">User registered</div>
                  <div className="activity-time">5 hours ago</div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

/**
 * Profile Layout Example Component
 */
const ProfileLayoutExample = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'activity', label: 'Activity', count: 12 },
    { id: 'settings', label: 'Settings' }
  ];

  return (
    <div className="page-layout-sample profile-layout">
      <div className="profile-header">
        <div className="profile-avatar">👤</div>
        <div className="profile-info">
          <h1>John Doe</h1>
          <p className="profile-email">john.doe@example.com</p>
          <p className="profile-role">Software Engineer</p>
        </div>
        <div className="profile-actions">
          <SecondaryButton onClick={() => {}}>Edit Profile</SecondaryButton>
        </div>
      </div>
      <TabNav
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
      <div className="profile-content">
        <div className="profile-section">
          <h2>About</h2>
          <p>Experienced software engineer with expertise in cloud computing and AI technologies.</p>
        </div>
        <div className="profile-section">
          <h2>Skills</h2>
          <div className="tag-list">
            <span className="tag">React</span>
            <span className="tag">Node.js</span>
            <span className="tag">Azure</span>
            <span className="tag">Python</span>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * List/Table Layout Example Component
 */
const ListTableLayoutExample = () => {
  const [searchQuery, setSearchQuery] = useState('');

  const tableData = [
    { id: 1, name: 'John Doe', email: 'john@example.com', role: 'Admin', status: 'Active' },
    { id: 2, name: 'Jane Smith', email: 'jane@example.com', role: 'User', status: 'Active' },
    { id: 3, name: 'Bob Johnson', email: 'bob@example.com', role: 'User', status: 'Inactive' }
  ];

  return (
    <div className="page-layout-sample list-table-layout">
      <div className="list-header">
        <h1>Users</h1>
        <div className="list-actions">
          <input
            type="text"
            className="list-search-input"
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <PrimaryButton onClick={() => {}}>Add User</PrimaryButton>
        </div>
      </div>
      <div className="list-filters">
        <SecondaryButton size="small">All</SecondaryButton>
        <SecondaryButton size="small" variant="outline">Active</SecondaryButton>
        <SecondaryButton size="small" variant="outline">Inactive</SecondaryButton>
      </div>
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tableData.map(row => (
              <tr key={row.id}>
                <td>{row.name}</td>
                <td>{row.email}</td>
                <td>{row.role}</td>
                <td><span className={`status-badge ${row.status.toLowerCase()}`}>{row.status}</span></td>
                <td>
                  <SecondaryButton size="small" onClick={() => {}}>Edit</SecondaryButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="list-pagination">
        <SecondaryButton size="small" variant="outline">Previous</SecondaryButton>
        <span>Page 1 of 1</span>
        <SecondaryButton size="small" variant="outline">Next</SecondaryButton>
      </div>
    </div>
  );
};

/**
 * Settings Layout Example Component
 */
const SettingsLayoutExample = () => {
  return (
    <div className="page-layout-sample settings-layout">
      <div className="settings-header">
        <h1>Settings</h1>
        <p>Manage your account settings and preferences</p>
      </div>
      <div className="settings-content">
        <div className="settings-section">
          <h2>Account</h2>
          <div className="form-group">
            <label htmlFor="settings-email">Email Address</label>
            <input type="email" id="settings-email" defaultValue="user@example.com" />
          </div>
          <div className="form-group">
            <label htmlFor="settings-name">Full Name</label>
            <input type="text" id="settings-name" defaultValue="John Doe" />
          </div>
          <div className="form-actions">
            <PrimaryButton onClick={() => {}}>Save Changes</PrimaryButton>
          </div>
        </div>
        <div className="settings-section">
          <h2>Notifications</h2>
          <div className="settings-toggle-group">
            <label>
              <input type="checkbox" defaultChecked /> Email notifications
            </label>
            <label>
              <input type="checkbox" defaultChecked /> Push notifications
            </label>
            <label>
              <input type="checkbox" /> SMS notifications
            </label>
          </div>
        </div>
        <div className="settings-section">
          <h2>Security</h2>
          <div className="form-group">
            <label htmlFor="settings-password">New Password</label>
            <input type="password" id="settings-password" />
          </div>
          <div className="form-actions">
            <PrimaryButton onClick={() => {}}>Update Password</PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Checkout Layout Example Component
 */
const CheckoutLayoutExample = () => {
  return (
    <div className="page-layout-sample checkout-layout">
      <div className="checkout-header">
        <h1>Checkout</h1>
      </div>
      <div className="checkout-content">
        <div className="checkout-form">
          <h2>Shipping Information</h2>
          <div className="form-group">
            <label htmlFor="checkout-name">Full Name</label>
            <input type="text" id="checkout-name" placeholder="Enter your name" />
          </div>
          <div className="form-group">
            <label htmlFor="checkout-address">Address</label>
            <input type="text" id="checkout-address" placeholder="Enter your address" />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="checkout-city">City</label>
              <input type="text" id="checkout-city" placeholder="City" />
            </div>
            <div className="form-group">
              <label htmlFor="checkout-zip">ZIP Code</label>
              <input type="text" id="checkout-zip" placeholder="ZIP" />
            </div>
          </div>
          <h2>Payment Information</h2>
          <div className="form-group">
            <label htmlFor="checkout-card">Card Number</label>
            <input type="text" id="checkout-card" placeholder="1234 5678 9012 3456" />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="checkout-expiry">Expiry Date</label>
              <input type="text" id="checkout-expiry" placeholder="MM/YY" />
            </div>
            <div className="form-group">
              <label htmlFor="checkout-cvv">CVV</label>
              <input type="text" id="checkout-cvv" placeholder="123" />
            </div>
          </div>
        </div>
        <div className="checkout-summary">
          <h2>Order Summary</h2>
          <div className="summary-item">
            <span>Subtotal</span>
            <span>$99.00</span>
          </div>
          <div className="summary-item">
            <span>Shipping</span>
            <span>$5.00</span>
          </div>
          <div className="summary-item summary-total">
            <span>Total</span>
            <span>$104.00</span>
          </div>
          <PrimaryButton onClick={() => {}}>Complete Purchase</PrimaryButton>
        </div>
      </div>
    </div>
  );
};

/**
 * Pricing Layout Example Component
 */
const PricingLayoutExample = () => {
  const plans = [
    {
      name: 'Starter',
      price: '$9',
      features: ['10 Projects', '5GB Storage', 'Email Support']
    },
    {
      name: 'Professional',
      price: '$29',
      popular: true,
      features: ['Unlimited Projects', '50GB Storage', 'Priority Support', 'Advanced Analytics']
    },
    {
      name: 'Enterprise',
      price: '$99',
      features: ['Unlimited Everything', '500GB Storage', '24/7 Support', 'Custom Integrations']
    }
  ];

  return (
    <div className="page-layout-sample pricing-layout">
      <div className="pricing-header">
        <h1>Choose Your Plan</h1>
        <p>Select the perfect plan for your needs</p>
      </div>
      <div className="pricing-grid">
        {plans.map((plan, index) => (
          <div key={index} className={`pricing-card ${plan.popular ? 'popular' : ''}`}>
            {plan.popular && <div className="popular-badge">Most Popular</div>}
            <h2>{plan.name}</h2>
            <div className="pricing-price">
              <span className="price-amount">{plan.price}</span>
              <span className="price-period">/month</span>
            </div>
            <ul className="pricing-features">
              {plan.features.map((feature, idx) => (
                <li key={idx}>✓ {feature}</li>
              ))}
            </ul>
            <PrimaryButton onClick={() => {}}>Get Started</PrimaryButton>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Styling Mock Layouts Page
 * Showcases complete page layout samples with search and filtering
 */
const StylingMockLayouts = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const features = [
    { icon: '🚀', title: 'Fast Performance', description: 'Lightning-fast response times and optimized performance' },
    { icon: '🔒', title: 'Secure & Reliable', description: 'Enterprise-grade security with 99.9% uptime guarantee' },
    { icon: '📊', title: 'Analytics Dashboard', description: 'Real-time insights and comprehensive analytics' },
    { icon: '🌐', title: 'Global Scale', description: 'Scale seamlessly across multiple regions and languages' }
  ];

  const layoutCategories = [
    { id: 'all', label: 'All Layouts' },
    { id: 'marketing', label: 'Marketing' },
    { id: 'content', label: 'Content' },
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'form', label: 'Forms' },
    { id: 'ecommerce', label: 'E-commerce' },
    { id: 'profile', label: 'Profile' }
  ];

  const layouts = [
    {
      id: 'landing',
      title: 'Landing Page',
      category: 'marketing',
      description: 'Complete landing page with hero section, features grid, and call-to-action',
      render: () => (
        <div className="page-layout-sample landing-page-layout">
          <HeroSection
            title="Welcome to Our Platform"
            subtitle="Transform your business with our innovative solutions. Get started today and experience the difference."
            buttonText="Get Started"
            onButtonClick={() => {}}
          />
          <div className="page-section">
            <h2 className="section-title">Why Choose Us</h2>
            <FeatureGrid features={features} />
          </div>
          <CTASection
            title="Ready to Get Started?"
            subtitle="Join thousands of satisfied customers and start your journey today."
            primaryButton={{ text: 'Start Free Trial', onClick: () => {} }}
            secondaryButton={{ text: 'Learn More', onClick: () => {} }}
            guarantees={['14-day free trial', 'No credit card required', 'Cancel anytime']}
          />
        </div>
      ),
      code: `<div className="landing-page">
  <HeroSection title="Welcome..." />
  <FeatureGrid features={features} />
  <CTASection title="Ready to Get Started?" />
</div>`
    },
    {
      id: 'content',
      title: 'Content Page',
      category: 'content',
      description: 'Standard content page with header, sections, and rich content',
      render: () => (
        <div className="page-layout-sample content-page-layout">
          <div className="page-header">
            <h1>About Our Company</h1>
            <p className="page-subtitle">Learn more about our mission, vision, and values</p>
          </div>
          <div className="page-content">
            <section className="content-section">
              <h2>Our Mission</h2>
              <p>At B-esper, we believe in the power of artificial intelligence to transform businesses and create meaningful connections.</p>
            </section>
            <section className="content-section">
              <h2>Our Vision</h2>
              <p>Our vision is to make AI accessible, practical, and beneficial for businesses of all sizes.</p>
            </section>
          </div>
        </div>
      ),
      code: `<div className="content-page">
  <div className="page-header">
    <h1>About Our Company</h1>
  </div>
  <div className="page-content">
    <section className="content-section">
      <h2>Our Mission</h2>
      <p>Content...</p>
    </section>
  </div>
</div>`
    },
    {
      id: 'dashboard',
      title: 'Dashboard Layout',
      category: 'dashboard',
      description: 'Complete dashboard layout with tabs, sidebar navigation, and main content area',
      render: () => <DashboardLayoutExample />,
      code: `<div className="dashboard-layout">
  <div className="dashboard-header">
    <h1>Dashboard</h1>
    <PrimaryButton>New Item</PrimaryButton>
  </div>
  <div className="dashboard-content">
    <SidebarNav items={sidebarItems} />
    <main className="dashboard-main">
      <TabNav tabs={tabs} />
      <div className="dashboard-stats">...</div>
    </main>
  </div>
</div>`
    },
    {
      id: 'form',
      title: 'Form Page',
      category: 'form',
      description: 'Form page with header, form fields, and action buttons',
      render: () => (
        <div className="page-layout-sample form-page-layout">
          <div className="form-header">
            <h1>Create New Account</h1>
            <p>Fill in the form below to create your new account</p>
          </div>
          <div className="form-content">
            <form className="form-layout">
              <div className="form-group">
                <label htmlFor="name">Full Name</label>
                <input type="text" id="name" placeholder="Enter your full name" />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input type="email" id="email" placeholder="Enter your email" />
              </div>
              <div className="form-actions">
                <SecondaryButton onClick={() => {}}>Cancel</SecondaryButton>
                <PrimaryButton onClick={() => {}}>Create Account</PrimaryButton>
              </div>
            </form>
          </div>
        </div>
      ),
      code: `<div className="form-page">
  <div className="form-header">
    <h1>Create New Account</h1>
  </div>
  <form className="form-layout">
    <div className="form-group">
      <label>Full Name</label>
      <input type="text" />
    </div>
    <div className="form-actions">
      <SecondaryButton>Cancel</SecondaryButton>
      <PrimaryButton>Create Account</PrimaryButton>
    </div>
  </form>
</div>`
    },
    {
      id: 'article',
      title: 'Article/Blog Layout',
      category: 'content',
      description: 'Article layout with header, content, and sidebar',
      render: () => (
        <div className="page-layout-sample article-layout">
          <article className="article-content">
            <header className="article-header">
              <div className="article-meta">
                <span className="article-date">March 15, 2024</span>
                <span className="article-category">Technology</span>
              </div>
              <h1 className="article-title">The Future of Artificial Intelligence in Business</h1>
              <p className="article-excerpt">Discover how AI is transforming the way businesses operate.</p>
            </header>
            <div className="article-body">
              <p>Artificial intelligence has become one of the most transformative technologies of our time.</p>
              <h2>Key Benefits</h2>
              <p>Businesses that embrace AI are seeing significant improvements in efficiency.</p>
            </div>
          </article>
          <aside className="article-sidebar">
            <div className="sidebar-content">
              <h3>Related Articles</h3>
              <ul className="related-list">
                <li><a href="#">AI Best Practices</a></li>
                <li><a href="#">Machine Learning Basics</a></li>
              </ul>
            </div>
          </aside>
        </div>
      ),
      code: `<div className="article-layout">
  <article className="article-content">
    <header className="article-header">
      <h1>Article Title</h1>
    </header>
    <div className="article-body">
      <p>Article content...</p>
    </div>
  </article>
  <aside className="article-sidebar">
    <div className="sidebar-content">...</div>
  </aside>
</div>`
    },
    {
      id: 'list',
      title: 'List/Table Layout',
      category: 'dashboard',
      description: 'Data table layout with search, filters, and pagination',
      render: () => <ListTableLayoutExample />,
      code: `<div className="list-table-layout">
  <div className="list-header">
    <h1>Users</h1>
    <input type="text" placeholder="Search..." />
    <PrimaryButton>Add User</PrimaryButton>
  </div>
  <table className="data-table">
    <thead>...</thead>
    <tbody>...</tbody>
  </table>
  <div className="list-pagination">...</div>
</div>`
    },
    {
      id: 'profile',
      title: 'Profile Page',
      category: 'profile',
      description: 'User profile page with avatar, tabs, and content sections',
      render: () => <ProfileLayoutExample />,
      code: `<div className="profile-layout">
  <div className="profile-header">
    <div className="profile-avatar">👤</div>
    <h1>John Doe</h1>
  </div>
  <TabNav tabs={tabs} />
  <div className="profile-content">...</div>
</div>`
    },
    {
      id: 'settings',
      title: 'Settings Page',
      category: 'form',
      description: 'Settings page with grouped sections and form controls',
      render: () => <SettingsLayoutExample />,
      code: `<div className="settings-layout">
  <div className="settings-header">
    <h1>Settings</h1>
  </div>
  <div className="settings-content">
    <div className="settings-section">
      <h2>Account</h2>
      <div className="form-group">...</div>
    </div>
  </div>
</div>`
    },
    {
      id: 'checkout',
      title: 'Checkout Page',
      category: 'ecommerce',
      description: 'E-commerce checkout page with order summary and payment form',
      render: () => <CheckoutLayoutExample />,
      code: `<div className="checkout-layout">
  <div className="checkout-header">
    <h1>Checkout</h1>
  </div>
  <div className="checkout-content">
    <div className="checkout-form">...</div>
    <div className="checkout-summary">...</div>
  </div>
</div>`
    },
    {
      id: 'pricing',
      title: 'Pricing Page',
      category: 'marketing',
      description: 'Pricing page with tiered plans and feature comparison',
      render: () => <PricingLayoutExample />,
      code: `<div className="pricing-layout">
  <div className="pricing-header">
    <h1>Choose Your Plan</h1>
  </div>
  <div className="pricing-grid">
    <div className="pricing-card">...</div>
  </div>
</div>`
    }
  ];

  const filteredLayouts = layouts.filter(layout => {
    const matchesSearch = layout.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         layout.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         layout.category.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || layout.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <StylingMockLayout>
      <div className="styling-mock-layouts">
        <div className="styling-mock-section">
          <h2 className="styling-mock-section-title">Page Layouts</h2>
          <p className="styling-mock-section-description">
            Complete page layout samples that you can use as templates for your own pages. These layouts demonstrate real-world page structures with headers, content sections, navigation, and more.
          </p>
          
          {/* Search and Filter Controls */}
          <div className="layouts-search-filter">
            <div className="layouts-search">
              <input
                type="text"
                className="search-input"
                placeholder="Search layouts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="layouts-category-filter">
              {layoutCategories.map(category => (
                <SecondaryButton
                  key={category.id}
                  variant={selectedCategory === category.id ? "primary" : "outline"}
                  size="small"
                  onClick={() => setSelectedCategory(category.id)}
                  className={selectedCategory === category.id ? 'active' : ''}
                >
                  {category.label}
                </SecondaryButton>
              ))}
            </div>
          </div>
          
          {filteredLayouts.length === 0 && (
            <div className="layouts-no-results">
              <p>No layouts found matching your search.</p>
            </div>
          )}
        </div>

        {/* Render filtered layouts */}
        {filteredLayouts.map(layout => (
          <div key={layout.id} className="styling-mock-section">
            <h3 className="styling-mock-section-title">{layout.title}</h3>
            <p className="styling-mock-section-subtitle">
              {layout.description}
            </p>
            <div className="styling-mock-showcase">
              <div className="styling-mock-showcase-content">
                {layout.render()}
              </div>
              <div className="styling-mock-code-block">
                {layout.code}
              </div>
            </div>
          </div>
        ))}
      </div>
    </StylingMockLayout>
  );
};

export default StylingMockLayouts;
